import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * 絞り込み条件を URL クエリに保持する。
 * リロード・共有・戻る操作で復元できる（docs/03-architecture.md 3.3）。
 */
export const useUrlFilters = <T extends Record<string, string | string[] | undefined>>(
  defaults: T,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const out = { ...defaults } as Record<string, string | string[] | undefined>;
    for (const key of Object.keys(defaults)) {
      const raw = searchParams.get(key);
      if (raw == null) continue;
      out[key] = Array.isArray(defaults[key]) ? raw.split(',').filter(Boolean) : raw;
    }
    return out as T;
  }, [searchParams, defaults]);

  const setFilter = useCallback(
    (key: keyof T & string, value: string | string[] | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const serialized = Array.isArray(value) ? value.join(',') : value;
          if (!serialized) next.delete(key);
          else next.set(key, serialized);
          // 条件を変えたらページ位置は先頭に戻す
          next.delete('cursor');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilters = useCallback(
    (values: Partial<Record<keyof T & string, string | string[] | undefined>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, raw] of Object.entries(values) as [string, string | string[] | undefined][]) {
            const serialized: string = Array.isArray(raw) ? raw.join(',') : (raw ?? '');
            if (!serialized) next.delete(key);
            else next.set(key, serialized);
          }
          next.delete('cursor');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const reset = useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);

  /** 適用中の条件数（「絞込 3」の表示に使う） */
  const activeCount = useMemo(
    () =>
      Object.keys(defaults).filter((key) => {
        const value = searchParams.get(key);
        return value != null && value !== '' && key !== 'q' && key !== 'sort';
      }).length,
    [searchParams, defaults],
  );

  return { filters, setFilter, setFilters, reset, activeCount, searchParams };
};

/** 入力の反映を遅らせる（検索欄向け） */
export const useDebounced = <T>(value: T, delayMs = 300): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

/** 一覧の選択状態 */
export const useSelection = () => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return { selected, toggle, toggleAll, clear, count: selected.size };
};

/**
 * 実行中のみポーリングする。非表示タブでは停止する
 * （docs/03-architecture.md 3.3）。
 */
export const usePollingInterval = (isActive: boolean, intervalMs = 3000): number | false => {
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');

  useEffect(() => {
    const handler = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  return isActive && visible ? intervalMs : false;
};

/** メディアクエリ（PC/スマホの出し分け） */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    media.addEventListener('change', handler);
    setMatches(media.matches);
    return () => media.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');

/** 自動保存（文面編集の 5 秒間隔） */
export const useAutosave = <T>(
  value: T,
  save: (value: T) => Promise<void>,
  options: { delayMs?: number; enabled?: boolean } = {},
) => {
  const { delayMs = 5000, enabled = true } = options;
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const initial = useRef(true);
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    if (!enabled) return;
    if (initial.current) {
      initial.current = false;
      return;
    }
    const timer = setTimeout(async () => {
      setIsSaving(true);
      try {
        await save(latest.current);
        setSavedAt(new Date());
      } finally {
        setIsSaving(false);
      }
    }, delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs, enabled, save]);

  return { savedAt, isSaving };
};
